/**
 * reportDrill — from a clicked report slice to the deals-list filter that
 * shows exactly the deals behind it.
 *
 * The deals list filters with POST /deals/search: deal columns by name, and
 * child tables through dotted prefixes (`lease_deed_tracts.county`), where
 * several AND-ed rules on one prefix must hold for the SAME child row — the
 * same row-level meaning a report on that dataset has. So a slice maps across
 * exactly when every field it constrains has a deals-search name:
 *
 *   dataset            own field f                deal field deals.x   other joined
 *   deals              f (and its own dotted      -                    -
 *                      child filters as-is)
 *   lease_deed_tracts  lease_deed_tracts.f        x                    not exact
 *   anything else      not exact                  x                    not exact
 *
 * "Not exact" means no drill is offered. lease_deeds.* on the tracts dataset
 * is not exact either: as a separate prefix it is not tied to the same tract.
 * Payments, contact log and status history have no own-field prefix the deals
 * list's filter panel offers, so only their deal-field slices could map — and
 * their whole-widget slice ("deals with a payment") cannot, so they are left
 * out entirely.
 *
 * A child dataset's slice always carries `{field: prefix, operator: "exists"}`
 * when nothing else names the prefix: a tract report counts deals that HAVE
 * tracts, not every deal.
 *
 * Group values: exact equality, or for a month / quarter / year bucket on a
 * DATE field the period's [start, next start). Buckets on timestamps are
 * computed in the business time zone and an age band is relative to today;
 * neither is offered. A null group value is not offered either.
 */
import {
  cleanFilters,
  fieldOf,
  groupOutputKey,
  REPORT_ONLY_OPS,
  toFilterGroup,
} from "@warwick-investment-group/wig-react-library/reports";

/** Datasets whose own fields map to a deals-search child prefix. */
export const DRILL_CHILD_DATASETS = ["lease_deed_tracts"];

/** Child prefixes the deals list's filter panel knows (DEAL_FILTER_ENTITIES). */
const DEALS_LIST_PREFIXES = [
  "wells",
  "well_tracts",
  "lease_deeds",
  "lease_deed_tracts",
  "deal_entities",
  "approvals",
];

const DRILLABLE = new Set(["deals", ...DRILL_CHILD_DATASETS]);

/**
 * A report field key -> its deals-search name, or null when no exact name
 * exists (see the table above).
 */
export const drillField = (datasetKey, fieldKey) => {
  if (!fieldKey || !DRILLABLE.has(datasetKey)) return null;
  const [head, ...rest] = String(fieldKey).split(".");
  if (datasetKey === "deals") {
    if (!rest.length) return fieldKey;
    // The deals dataset's own child filters are already deals-search names.
    return DEALS_LIST_PREFIXES.includes(head) ? fieldKey : null;
  }
  if (!rest.length) return `${datasetKey}.${fieldKey}`;
  if (head === "deals" && rest.length === 1) return rest[0];
  return null;
};

/**
 * Rename every leaf of a filter tree to its deals-search field. Null if any
 * leaf has no exact name. Exists / nexists on a bare child prefix (the deals
 * dataset's "has any / has none") pass through.
 */
export const drillFilters = (node, datasetKey) => {
  if (!node) return null;
  if (Array.isArray(node.rules)) {
    const rules = [];
    for (const r of node.rules) {
      const mapped = drillFilters(r, datasetKey);
      if (!mapped) return null;
      rules.push(mapped);
    }
    return { combinator: node.combinator === "or" ? "or" : "and", rules };
  }
  const bareChild =
    datasetKey === "deals" &&
    DEALS_LIST_PREFIXES.includes(node.field) &&
    (node.operator === "exists" || node.operator === "nexists");
  if (bareChild) return { field: node.field, operator: node.operator };
  // Relative dates and categories only mean something to the report engine;
  // the deals list needs them resolved (the query's resolved_filters).
  if (REPORT_ONLY_OPS.includes(node.operator)) return null;
  const field = drillField(datasetKey, node.field);
  return field ? { field, operator: node.operator, value: node.value ?? null } : null;
};

const pad = (n) => String(n).padStart(2, "0");

/** [start, next start) of a bucket period, as YYYY-MM-DD, or null. */
export const bucketRange = (value, bucket) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const months = { month: 1, quarter: 3, year: 12 }[bucket];
  if (!months) return null;
  const startMonth = bucket === "year" ? 1 : bucket === "quarter" ? Math.floor((mo - 1) / 3) * 3 + 1 : mo;
  const total = startMonth - 1 + months;
  const ny = y + Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return [`${y}-${pad(startMonth)}-01`, `${ny}-${pad(nm)}-01`];
};

/** Rules for one group-by term at `value`, or null when not exact. */
export const groupRules = (g, value, datasetKey, dataset) => {
  if (value === null || value === undefined || value === "") return null;
  const field = drillField(datasetKey, g?.field);
  if (!field) return null;
  if (!g.bucket) return [{ field, operator: "eq", value }];
  const f = fieldOf(dataset, g.field);
  if (g.bucket === "age_band" || f?.data_type !== "Date") return null;
  const range = bucketRange(value, g.bucket);
  if (!range) return null;
  return [
    { field, operator: "gte", value: range[0] },
    { field, operator: "lt", value: range[1] },
  ];
};

/**
 * The deals-list filter for a slice of a widget, or null when it cannot be
 * expressed exactly.
 *
 * @param {object} args
 * @param {object} args.query    the widget query AS RUN (report parameters
 *                               already merged in)
 * @param {object} [args.dataset] its catalog dataset (field types for buckets)
 * @param {object} [args.row]    the clicked result row; omit for the whole
 *                               widget (a KPI tile)
 * @param {string[]} [args.groupKeys] which group outputs of the row to pin;
 *                               default every group-by of the query
 * @returns {{combinator: "and", rules: Array}|null}
 */
export const buildDrillFilter = ({ query, dataset, row, groupKeys } = {}) => {
  const datasetKey = query?.dataset;
  if (!DRILLABLE.has(datasetKey)) return null;

  const rules = [];
  const own = cleanFilters(toFilterGroup(query.filters));
  if (own) {
    const mapped = drillFilters(own, datasetKey);
    if (!mapped) return null;
    // On a child dataset an OR cannot keep the same-row meaning the report
    // has (each branch would be matched against any child row).
    const hasOr = (n) => Array.isArray(n.rules) && (n.combinator === "or" || n.rules.some(hasOr));
    if (datasetKey !== "deals" && hasOr(mapped)) return null;
    // A plain AND group is flattened so the deals list shows simple chips.
    if (mapped.combinator === "and") rules.push(...mapped.rules);
    else rules.push(mapped);
  }

  if (row) {
    const groups = Array.isArray(query.group_by) ? query.group_by.filter((g) => g?.field) : [];
    for (const g of groups) {
      const key = groupOutputKey(g);
      if (groupKeys && !groupKeys.includes(key)) continue;
      const r = groupRules(g, row[key], datasetKey, dataset);
      if (!r) return null;
      rules.push(...r);
    }
  }

  // A child dataset's slice is deals that HAVE such a child row.
  if (datasetKey !== "deals") {
    const prefix = `${datasetKey}.`;
    const named = (node) =>
      Array.isArray(node.rules) ? node.rules.some(named) : String(node.field).startsWith(prefix);
    if (!rules.some(named)) {
      // No `value`: the deals list's own "has any" chips carry none.
      rules.push({ field: datasetKey, operator: "exists" });
    }
  }

  return { combinator: "and", rules };
};

export default buildDrillFilter;
